import { useCallback, useEffect, useState } from "react";

const FAV_KEY = "tangxin:favorites";
const HIS_KEY = "tangxin:history";

const CHANGE_EVENT = "tangxin:library-change";

function readList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function writeList(key: string, list: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* ignore quota errors in demo */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function getFavorites(): string[] {
  return readList(FAV_KEY);
}

export function isFavorite(id: string): boolean {
  return readList(FAV_KEY).includes(id);
}

export function toggleFavorite(id: string): boolean {
  const list = readList(FAV_KEY);
  const idx = list.indexOf(id);
  if (idx >= 0) {
    list.splice(idx, 1);
    writeList(FAV_KEY, list);
    return false;
  }
  list.unshift(id);
  writeList(FAV_KEY, list);
  return true;
}

export function removeFavorite(id: string): void {
  const list = readList(FAV_KEY).filter((x) => x !== id);
  writeList(FAV_KEY, list);
}

export function clearFavorites(): void {
  writeList(FAV_KEY, []);
}

export interface HistoryEntry {
  id: string;
  visitedAt: string;
}

export function getHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HIS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is HistoryEntry =>
        x && typeof x.id === "string" && typeof x.visitedAt === "string",
    );
  } catch {
    return [];
  }
}

export function addHistory(id: string): void {
  const list = getHistory().filter((e) => e.id !== id);
  list.unshift({ id, visitedAt: new Date().toISOString() });
  writeList(HIS_KEY, list.slice(0, 60) as unknown as string[]);
}

export function clearHistory(): void {
  writeList(HIS_KEY, []);
}

/** Subscribe to any change of favorites/history. */
export function useLibraryVersion(): number {
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  useEffect(() => {
    window.addEventListener(CHANGE_EVENT, bump);
    window.addEventListener("storage", bump);
    return () => {
      window.removeEventListener(CHANGE_EVENT, bump);
      window.removeEventListener("storage", bump);
    };
  }, [bump]);
  return version;
}